import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Briefcase, Plus, X, Mail, Phone, MapPin, Link2, AlertTriangle, MessageSquare, Loader2, Sparkles, ChevronRight, Send, RefreshCw, Trash2, Calculator, Webhook, Copy, RotateCw, FileText, Globe, ExternalLink, Calendar, Pencil, Search, GitBranch, Activity } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { API_BASE, formatApiError, useAuth } from "../components/AuthContext";
import ComposeEmailDialog from "../components/ComposeEmailDialog";
import AssigneePicker, { AssigneeAvatar, useTeamMembers } from "../components/AssigneePicker";
import TasksPanel from "../components/TasksPanel";
import CommentsPanel from "../components/CommentsPanel";
import LeadReferralsSection from "../components/LeadReferralsSection";
import ActivityTimeline from "../components/ActivityTimeline";
import { checkIdGaps } from "../components/IdGapHost";

const STAGES = [
  { key: "new",                   label: "New",        accent: "var(--ink-3)" },
  { key: "information_requested", label: "Info req'd", accent: "var(--accent)" },
  { key: "documents_received",    label: "Docs in",    accent: "var(--moss)" },
  { key: "in_preparation",        label: "Preparing",  accent: "var(--ink)" },
  { key: "awaiting_review",       label: "Review",     accent: "var(--ink)" },
  { key: "lodged",                label: "Lodged",     accent: "var(--moss)" },
  { key: "deferred",              label: "Deferred",   accent: "#B91C1C" },
  { key: "lost",                  label: "Lost",       accent: "var(--ink-3)" },
];

const SOURCES = [
  { key: "email",        label: "Email" },
  { key: "whatsapp",     label: "WhatsApp" },
  { key: "phone",        label: "Phone" },
  { key: "referral",     label: "Referral" },
  { key: "website_form", label: "Website form" },
  { key: "viber",        label: "Viber" },
  { key: "walk_in",      label: "Walk-in" },
  { key: "manual",       label: "Manual" },
];

const PAYMENT_STATES = ["unpaid", "partial", "paid", "refunded"];

const INPUT_CLS = "w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[14px] focus:outline-none focus:border-[var(--ink)]";

const tagLabel = (t) => {
  // Specific labels for tags whose machine_name differs visually from the desired label
  const overrides = {
    self_education_expenses: "Self-education expenses",
    super_refund: "Super refund",
    uber_ride: "UBER Ride",
    didi: "DIDI",
    ola: "Ola",
    air_bnb: "Air BnB",
    airtasker: "Airtasker",
    abn_income: "ABN income",
    psi: "PSI",
    multi_year_return: "Multi-year return",
  };
  if (overrides[t]) return overrides[t];
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const AU_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

const DEFERRAL_REASONS = [
  { key: "payable",                  label: "Payable" },
  { key: "no_funds",                 label: "No funds to pay service fee" },
  { key: "incomplete_information",   label: "Incomplete information" },
  { key: "partner_income_missing",   label: "Partner's income not available" },
  { key: "other",                    label: "Other (specify)" },
];

export default function Leads() {
  const { state } = useAuth();
  const myId = state?.user?.user_id;
  const [leads, setLeads] = useState([]);
  const [tags, setTags] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [openWebhook, setOpenWebhook] = useState(false);
  const [openIntake, setOpenIntake] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [dragId, setDragId] = useState(null);       // lead being dragged
  const [dragOverStage, setDragOverStage] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState("");   // v68.23 — free-text search
  const [debouncedQ, setDebouncedQ] = useState("");
  const members = useTeamMembers();

  const memberById = useMemo(
    () => Object.fromEntries(members.map((m) => [m.user_id, m])),
    [members],
  );

  // URL-driven filters (drill-through from Reports)
  const filters = useMemo(() => ({
    status:      searchParams.get("status") || "",
    assignee_id: searchParams.get("assignee_id") || "",
    source:      searchParams.get("source") || "",
    city:        searchParams.get("city") || "",
  }), [searchParams]);

  const clearFilters = () => setSearchParams({});

  // Optimistic drag-drop: move card → PATCH status → reload (or revert on error)
  const onCardDrop = useCallback(async (leadId, targetStage) => {
    setDragOverStage(null);
    setDragId(null);
    const lead = leads.find((l) => l.lead_id === leadId);
    if (!lead || lead.status === targetStage) return;
    // Optimistic UI
    setLeads((prev) => prev.map((l) => l.lead_id === leadId ? { ...l, status: targetStage } : l));
    try {
      await axios.post(`${API_BASE}/leads/${leadId}/status`, { status: targetStage });
      toast.success(`Moved → ${targetStage.replace(/_/g, " ")}`);
    } catch (err) {
      // Revert on failure
      setLeads((prev) => prev.map((l) => l.lead_id === leadId ? { ...l, status: lead.status } : l));
      toast.error(formatApiError(err) || "Could not update stage");
    }
  }, [leads]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(filters)) if (v) qs.append(k, v);
      if (debouncedQ) qs.append("q", debouncedQ);
      const path = qs.toString() ? `${API_BASE}/leads?${qs}` : `${API_BASE}/leads`;
      const [l, t, svc] = await Promise.all([
        axios.get(path),
        axios.get(`${API_BASE}/leads/tags`),
        axios.get(`${API_BASE}/firm-services`),
      ]);
      setLeads(l.data); setTags(t.data.tags || []); setServices(svc.data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [filters, debouncedQ]);

  useEffect(() => { reload(); }, [reload]);

  // Debounce the search input so we don't fire a request per keystroke
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const byStage = useMemo(() => {
    const m = Object.fromEntries(STAGES.map((s) => [s.key, []]));
    for (const l of leads) (m[l.status] || m.new).push(l);
    return m;
  }, [leads]);

  const activeFilters = useMemo(
    () => Object.entries(filters).filter(([, v]) => v),
    [filters]
  );
  const hasActiveFilters = activeFilters.length > 0;

  return (
    <div className="px-10 py-12 max-w-[1600px]" data-testid="leads-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="overline mb-2">Phase 1 · Lead pipeline</div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>
            Lead <em className="italic" style={{ color: "var(--accent)" }}>pipeline</em>
          </h1>
          <p className="text-[var(--ink-2)] mt-2 max-w-2xl">
            Every enquiry from first contact to lodgement. Add manually or auto-create from email triage.
            Lodged leads convert to a client + job automatically.
          </p>
          {hasActiveFilters && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--ink)] text-[var(--paper)] text-[11px]" data-testid="active-filter-chip">
              <span>Filtered:</span>
              {activeFilters.map(([k, v]) => (
                <span key={k} className="font-mono">{k}={v}</span>
              ))}
              <button onClick={clearFilters} className="hover:text-[var(--accent)] inline-flex items-center gap-1" data-testid="active-filter-clear">
                <X size={11}/> Clear
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search name, email, phone, TFN…"
              data-testid="leads-search-input"
              className="text-[12px] pl-8 pr-3 py-1.5 rounded-full border border-[var(--border)] bg-white w-64 focus:outline-none focus:border-[var(--ink)]"
            />
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
            {searchInput && (
              <button
                onClick={() => setSearchInput("")}
                data-testid="leads-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--ink-3)] hover:text-[var(--ink)]"
                title="Clear"
              >
                <X size={11} />
              </button>
            )}
          </div>
          <button
            onClick={() => setSearchParams(filters.assignee_id === "me" ? {} : { assignee_id: "me" })}
            data-testid="leads-my-work-toggle"
            className={`text-[11px] inline-flex items-center gap-1 rounded-full px-3 py-1.5 transition ${
              filters.assignee_id === "me"
                ? "bg-[var(--ink)] text-[var(--paper)] border border-[var(--ink)]"
                : "border border-[var(--border)] text-[var(--ink-2)] hover:border-[var(--ink)]"
            }`}
          >
            <AssigneeAvatar user={members.find((m) => m.user_id === myId)} size={14} /> My work
          </button>
          <button onClick={() => setOpenIntake(true)} className="btn-secondary" data-testid="lead-intake-btn">
            <Globe size={14}/> Intake form
          </button>
          <button onClick={() => setOpenWebhook(true)} className="btn-secondary" data-testid="lead-webhook-btn">
            <Webhook size={14}/> Webhook
          </button>
          <button onClick={() => setOpenCreate(true)} className="btn-primary" data-testid="lead-create-btn">
            <Plus size={14} /> New lead
          </button>
        </div>
      </div>

      {loading && !leads.length ? (
        <div className="text-[var(--ink-3)] text-sm">Loading…</div>
      ) : (
        <div className="grid gap-4 overflow-x-auto pb-4" style={{ gridTemplateColumns: `repeat(${STAGES.length}, minmax(240px, 1fr))` }} data-testid="leads-kanban">
          {(() => {
            // v68.26 — precompute the ITR service_id set once so the red-strip
            // check on each card is O(1) instead of O(services).
            const itrIds = new Set(
              (services || [])
                .filter((s) => (s.code || "").toUpperCase() === "ITR")
                .map((s) => s.service_id),
            );
            return STAGES.map((stage) => (
            <div key={stage.key} className="flex flex-col" data-testid={`lane-${stage.key}`}
                 onDragOver={(e) => { e.preventDefault(); setDragOverStage(stage.key); }}
                 onDragLeave={() => setDragOverStage((cur) => cur === stage.key ? null : cur)}
                 onDrop={(e) => { e.preventDefault(); if (dragId) onCardDrop(dragId, stage.key); }}>
              <div className="flex items-center justify-between mb-2 px-1">
                <div className="overline text-[10px]" style={{ color: stage.accent }}>{stage.label}</div>
                <span className="text-[11px] font-mono text-[var(--ink-3)]">{byStage[stage.key]?.length || 0}</span>
              </div>
              <div className={`space-y-2 min-h-[80px] rounded-md p-1 transition-colors ${
                dragOverStage === stage.key && dragId ? "bg-[var(--accent-light)] outline outline-2 outline-[var(--accent)]" : ""
              }`}>
                {(byStage[stage.key] || []).map((l) => {
                  const hasWorkflow = !!l.workflow_template_id;
                  const isITR = (l.service_ids || []).some((sid) => itrIds.has(sid));
                  const unpaidItrStrip = isITR && l.payment_status !== "paid";
                  return (
                  <button
                    key={l.lead_id}
                    onClick={() => setSelectedId(l.lead_id)}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", l.lead_id);
                      setDragId(l.lead_id);
                    }}
                    onDragEnd={() => { setDragId(null); setDragOverStage(null); }}
                    data-testid={`lead-card-${l.lead_id}`}
                    data-workflow={hasWorkflow ? "1" : "0"}
                    data-unpaid-itr={unpaidItrStrip ? "1" : "0"}
                    className={`w-full text-left card p-3 hover:border-[var(--ink)] transition cursor-grab active:cursor-grabbing overflow-hidden relative ${
                      hasWorkflow
                        ? "border-[2px] border-[#2563EB] bg-[#EFF6FF]"
                        : "border border-[var(--border)]"
                    } ${dragId === l.lead_id ? "opacity-50" : ""}`}
                  >
                    {unpaidItrStrip && (
                      <span
                        aria-hidden
                        data-testid={`lead-card-${l.lead_id}-unpaid-itr-strip`}
                        title="Individual Tax Return · payment outstanding"
                        className="absolute inset-x-0 top-0 h-1"
                        style={{ background: "#DC2626" }}
                      />
                    )}
                    <div className="text-[13px] font-medium text-[var(--ink)] truncate">{l.name}</div>
                    <div className="text-[11px] text-[var(--ink-3)] mt-0.5 flex items-center gap-1">
                      <span className="capitalize">{l.source.replace("_", " ")}</span>
                      {l.city && <><span>·</span><MapPin size={9}/><span>{l.city}</span></>}
                    </div>
                    {hasWorkflow && (
                      <div
                        className="mt-2 inline-flex items-center gap-1 text-[10px] font-medium rounded-full px-2 py-0.5"
                        style={{ background: "#DBEAFE", color: "#1E40AF" }}
                        data-testid={`lead-card-${l.lead_id}-workflow-badge`}
                      >
                        <GitBranch size={9} />
                        <span className="truncate max-w-[140px]">{l.workflow_template_name || "Workflow attached"}</span>
                      </div>
                    )}
                    {l.scenario_tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {l.scenario_tags.slice(0, 3).map((t) => (
                          <span key={t} className="badge" style={{ background: "var(--subtle)", fontSize: "9px" }}>{tagLabel(t)}</span>
                        ))}
                        {l.scenario_tags.length > 3 && <span className="text-[9px] text-[var(--ink-3)]">+{l.scenario_tags.length - 3}</span>}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-2 text-[10px] text-[var(--ink-3)]">
                      <span className="font-mono">{l.payment_status === "paid" ? "✓ paid" : l.payment_status}</span>
                      <div className="flex items-center gap-2">
                        {l.estimated_fee && <span className="font-mono">${l.estimated_fee}</span>}
                        <AssigneeAvatar user={memberById[l.assignee_id]} size={16} />
                      </div>
                    </div>
                  </button>
                  );
                })}
              </div>
            </div>
          ));
          })()}
        </div>
      )}

      {openCreate && (
        <CreateLeadModal
          tags={tags}
          onClose={() => setOpenCreate(false)}
          onCreated={() => { setOpenCreate(false); reload(); }}        />
      )}
      {openWebhook && (
        <WebhookConfigModal onClose={() => setOpenWebhook(false)}/>
      )}
      {openIntake && (
        <IntakeFormConfigModal onClose={() => setOpenIntake(false)}/>
      )}
      {selectedId && (
        <LeadDetailDrawer
          leadId={selectedId}
          tags={tags}
          services={services}
          onClose={() => setSelectedId(null)}
          onUpdated={reload}
        />
      )}
    </div>
  );
}

function CreateLeadModal({ tags, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", phone_is_whatsapp: false,
    source: "phone", state: "",
    scenario_tags: [], notes: "", multi_year_count: "",
  });
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);

  const toggleTag = (t) => setForm((f) => ({
    ...f,
    scenario_tags: f.scenario_tags.includes(t) ? f.scenario_tags.filter((x) => x !== t) : [...f.scenario_tags, t],
  }));

  const submit = async (force = false) => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.multi_year_count) payload.multi_year_count = parseInt(payload.multi_year_count, 10);
      else delete payload.multi_year_count;
      if (!payload.email) delete payload.email;
      if (!payload.phone) delete payload.phone;
      if (!payload.state) delete payload.state;
      if (!payload.notes) delete payload.notes;
      if (!payload.phone_is_whatsapp) delete payload.phone_is_whatsapp;
      const url = `${API_BASE}/leads${force ? "?force=true" : ""}`;
      await axios.post(url, payload);
      toast.success("Lead created");
      onCreated();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "duplicate_warning") {
        setDup(detail);
      } else { toast.error(formatApiError(err)); }
    } finally { setBusy(false); }
  };

  const showMultiYear = form.scenario_tags.includes("multi_year_return");

  return (
    <div className="fixed inset-0 z-50 bg-[var(--ink)]/40 flex items-center justify-center p-6" onClick={onClose} data-testid="lead-create-modal">
      <div className="bg-[var(--paper)] rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[var(--border)]" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[var(--border)] flex items-center justify-between">
          <h2 className="font-heading text-2xl" style={{ fontWeight: 500 }}>New lead</h2>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="lead-modal-close"><X size={18} /></button>
        </div>

        {dup && (
          <div className="mx-6 mt-4 p-3 border border-[#B91C1C]/30 rounded-md flex items-start gap-3" style={{ background: "rgba(166,59,34,0.05)" }} data-testid="lead-duplicate-warning">
            <AlertTriangle size={16} style={{ color: "#B91C1C" }} className="mt-0.5"/>
            <div className="flex-1">
              <div className="text-[13px] font-medium text-[var(--ink)]">Possible duplicate</div>
              <div className="text-[12px] text-[var(--ink-2)] mt-1">
                <b>{dup.match.name}</b> ({dup.match.email || dup.match.phone}) was created {new Date(dup.match.created_at).toLocaleDateString()} — current status: <i>{dup.match.status.replace("_", " ")}</i>.
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => submit(true)} disabled={busy} className="text-[11px] btn-secondary" data-testid="lead-create-anyway">Create anyway</button>
                <button onClick={() => setDup(null)} className="text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)]">Edit details</button>
              </div>
            </div>
          </div>
        )}

        <div className="p-6 space-y-4">
          <Field label="Name *">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={INPUT_CLS} data-testid="lead-name-input" autoFocus />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email">
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={INPUT_CLS} data-testid="lead-email-input" />
            </Field>
            <Field label="Phone">
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={INPUT_CLS} data-testid="lead-phone-input" />
              <label className="flex items-center gap-1.5 mt-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.phone_is_whatsapp}
                  onChange={(e) => setForm({ ...form, phone_is_whatsapp: e.target.checked })}
                  className="accent-[var(--accent)]"
                  data-testid="lead-phone-whatsapp"
                />
                <span className="text-[10px] text-[var(--ink-3)]">This number is on WhatsApp</span>
              </label>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Source">
              <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className={INPUT_CLS} data-testid="lead-source-select">
                {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="State / Territory">
              <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={INPUT_CLS} data-testid="lead-state-select">
                <option value="">— Select —</option>
                {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Interested services">
            <div className="flex flex-wrap gap-2" data-testid="lead-tags-selector">
              {tags.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleTag(t)}
                  data-testid={`lead-tag-${t}`}
                  className={`text-[11px] px-2 py-1 rounded-full border transition ${
                    form.scenario_tags.includes(t)
                      ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]"
                      : "border-[var(--border)] text-[var(--ink-2)] hover:border-[var(--ink)]"
                  }`}
                >
                  {tagLabel(t)}
                </button>
              ))}
            </div>
          </Field>
          {showMultiYear && (
            <Field label="Multi-year return — how many years?">
              <input type="number" min={1} max={10} value={form.multi_year_count}
                onChange={(e) => setForm({ ...form, multi_year_count: e.target.value })}
                className={`${INPUT_CLS} w-24`} data-testid="lead-multiyear-input" />
            </Field>
          )}
          <div className="text-[11px] text-[var(--ink-3)] italic">
            <Calculator size={11} className="inline mr-1 -mt-0.5"/>
            Fee will auto-calculate from base + tag addons + multi-year. You can add manual items after creating.
          </div>
          <Field label="Notes (internal)">
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className={INPUT_CLS} data-testid="lead-notes-input"/>
          </Field>
        </div>

        <div className="p-6 border-t border-[var(--border)] flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary" data-testid="lead-modal-cancel">Cancel</button>
          <button onClick={() => submit(false)} disabled={busy} className="btn-primary" data-testid="lead-modal-submit">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>}
            {busy ? "Creating…" : "Create lead"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline mb-1 text-[10px]">{label}</div>
      {children}
    </label>
  );
}


function DeferLeadModal({ onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  const [reasonOther, setReasonOther] = useState("");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);

  const isOther = reason === "other";
  const minDate = new Date().toISOString().slice(0, 10);

  const submit = async () => {
    if (!reason) { toast.error("Pick a deferral reason"); return; }
    if (isOther && !reasonOther.trim()) { toast.error("Describe the reason"); return; }
    if (!date) { toast.error("Pick a follow-up date"); return; }
    setBusy(true);
    try {
      await onSubmit({
        reason,
        reason_other: isOther ? reasonOther.trim() : null,
        follow_up_at: date,
      });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[65] bg-[var(--ink)]/40 flex items-center justify-center p-6"
         onClick={onClose} data-testid="defer-modal">
      <div className="bg-[var(--paper)] rounded-lg w-full max-w-md border border-[var(--border)]"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[var(--border)] flex items-center justify-between">
          <h2 className="font-heading text-xl" style={{ fontWeight: 500 }}>Defer this lead</h2>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="defer-close">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Reason">
            <select value={reason} onChange={(e) => setReason(e.target.value)}
                    className={INPUT_CLS} data-testid="defer-reason-select">
              <option value="">— Pick a reason —</option>
              {DEFERRAL_REASONS.map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>
          </Field>
          {isOther && (
            <Field label="Describe">
              <input value={reasonOther} onChange={(e) => setReasonOther(e.target.value)}
                     className={INPUT_CLS} placeholder="Why is this lead being deferred?"
                     data-testid="defer-reason-other" autoFocus />
            </Field>
          )}
          <Field label="Follow-up date">
            <input type="date" min={minDate} value={date}
                   onChange={(e) => setDate(e.target.value)}
                   className={INPUT_CLS} data-testid="defer-followup-date" />
          </Field>
        </div>
        <div className="p-5 border-t border-[var(--border)] flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary" data-testid="defer-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary" data-testid="defer-submit">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Calendar size={14}/>}
            {busy ? "Deferring…" : "Defer lead"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LeadDetailDrawer({ leadId, tags, services = [], onClose, onUpdated }) {
  const [lead, setLead] = useState(null);
  const [busy, setBusy] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [deferModal, setDeferModal] = useState(null);  // { extras } when open

  const reload = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/leads/${leadId}`);
      setLead(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
  }, [leadId]);

  useEffect(() => { reload(); }, [reload]);

  const setStatus = async (newStatus, extras = {}, force = false) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/leads/${leadId}/status`, { status: newStatus, force, ...extras });
      setLead(r.data);
      onUpdated();
      if (newStatus === "lodged" && r.data?.client_id) {
        toast.success(`Lodged — converted to client + job`);
      } else { toast.success(`Moved to ${newStatus.replace("_", " ")}`); }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail.includes("Deferred")) {
        // Open the structured defer modal — dropdown reason + calendar date
        setDeferModal({ newStatus });
        return;
      }
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const patch = async (diff) => {
    try {
      const r = await axios.patch(`${API_BASE}/leads/${leadId}`, diff);
      setLead(r.data); onUpdated();
      toast.success("Updated");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await axios.post(`${API_BASE}/leads/${leadId}/notes`, { body: noteText.trim() });
      setNoteText("");
      reload();
      toast.success("Note added");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this lead permanently? Its history will be lost.")) return;
    try {
      await axios.delete(`${API_BASE}/leads/${leadId}`);
      onUpdated();
      onClose();
      toast.success("Lead deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!lead) return null;

  return (
    <div className="fixed inset-0 z-50 bg-[var(--ink)]/30 flex justify-end" onClick={onClose} data-testid="lead-detail-drawer">
      <div className="bg-[var(--paper)] w-full max-w-3xl lg:max-w-4xl h-full overflow-y-auto border-l border-[var(--border)]" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 p-6 border-b border-[var(--border)] bg-[var(--paper)] flex items-start justify-between z-10">
          <div>
            <h2 className="font-heading text-2xl" style={{ fontWeight: 500 }} data-testid="lead-detail-name">{lead.name}</h2>
            <div className="text-[11px] text-[var(--ink-3)] font-mono mt-1">{lead.lead_id}</div>
            <div className="mt-2">
              <AssigneePicker
                value={lead.assignee_id}
                onChange={(uid) => patch({ assignee_id: uid })}
                testid="lead-detail-assignee"
                label="Assign to teammate"
              />
            </div>
          </div>
          <div className="flex items-start gap-3">
            {lead.status !== "dropped" ? (
              <button
                onClick={async () => {
                  if (!window.confirm("Dismiss this lead as 'not a lead'? It will move to the Dropped column.")) return;
                  await patch({ status: "dropped" });
                  toast.success("Marked as not a lead");
                }}
                data-testid="lead-detail-not-a-lead"
                title="Move this lead to Dropped"
                className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-[var(--border)] text-[var(--ink-2)] hover:border-[#B91C1C] hover:text-[#B91C1C] transition"
              >
                Not a lead
              </button>
            ) : (
              <button
                onClick={async () => { await patch({ status: "new" }); toast.success("Restored to New"); }}
                data-testid="lead-detail-restore"
                className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border border-[var(--border)] text-[var(--ink-2)] hover:border-[var(--ink)] hover:text-[var(--ink)] transition"
              >
                Restore
              </button>
            )}
            <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="lead-detail-close"><X size={20}/></button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Stage transitions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="overline">Move to stage</div>
              {lead.email && (
                <button onClick={() => setSendingEmail(true)} className="text-[11px] text-[var(--accent)] hover:underline inline-flex items-center gap-1" data-testid="lead-send-email-btn">
                  <Send size={11}/> Send email
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2" data-testid="lead-stage-actions">
              {STAGES.filter((s) => s.key !== lead.status).map((s) => (
                <button key={s.key} disabled={busy} onClick={() => setStatus(s.key)}
                  data-testid={`lead-move-${s.key}`}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-[var(--border)] hover:border-[var(--ink)] inline-flex items-center gap-1"
                  style={{ color: s.accent }}>
                  {s.label} <ChevronRight size={11}/>
                </button>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-2 gap-3">
            {lead.email && (
              <div><div className="overline mb-0.5">Email</div><div className="text-[13px] inline-flex items-center gap-1"><Mail size={11}/> {lead.email}</div></div>
            )}
            {lead.phone && (
              <div><div className="overline mb-0.5">Phone</div><div className="text-[13px] inline-flex items-center gap-1"><Phone size={11}/> {lead.phone}</div></div>
            )}
            <div><div className="overline mb-0.5">Source</div><div className="text-[13px] capitalize">{lead.source.replace("_", " ")}</div></div>
            {lead.city && <div><div className="overline mb-0.5">City</div><div className="text-[13px]">{lead.city}</div></div>}
          </div>

          {/* Payment + fee */}
          <div className="border-t border-[var(--border)] pt-4">
            <div className="overline mb-2">Fee & payment</div>
            <div className="grid grid-cols-2 gap-3 items-end mb-3">
              <Field label="Payment status">
                <select value={lead.payment_status} onChange={(e) => patch({ payment_status: e.target.value })} className={INPUT_CLS} data-testid="lead-detail-payment-status">
                  {PAYMENT_STATES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Amount paid">
                <input type="number" step="0.01" defaultValue={lead.amount_paid || 0} onBlur={(e) => {
                  const v = parseFloat(e.target.value) || 0;
                  if (v !== lead.amount_paid) patch({ amount_paid: v });
                }} className={INPUT_CLS} data-testid="lead-detail-paid"/>
              </Field>
              <Field label="Estimated deal value (annual)">
                <input
                  type="number" step="0.01" min="0"
                  defaultValue={lead.estimated_value || ""}
                  onBlur={(e) => {
                    const raw = e.target.value;
                    const v = raw === "" ? null : (parseFloat(raw) || 0);
                    if (v !== (lead.estimated_value ?? null)) patch({ estimated_value: v });
                  }}
                  placeholder="$ value if won"
                  className={INPUT_CLS} data-testid="lead-detail-estimated-value"/>
              </Field>
              {!lead.client_id && (
                <div>
                  <div className="overline mb-0.5">&nbsp;</div>
                  <ConvertButton lead={lead} onConverted={(updated) => { setLead(updated); onUpdated(); }} />
                </div>
              )}
              {lead.client_id && (
                <div>
                  <div className="overline mb-0.5">Converted</div>
                  <div className="text-[12px] text-[var(--ink-2)]" data-testid="lead-detail-converted-info">
                    Client: <span className="font-mono">{lead.client_id.slice(-6)}</span>
                    {lead.job_id && <> · Job: <span className="font-mono">{lead.job_id.slice(-6)}</span></>}
                  </div>
                </div>
              )}
              <div>
                <div className="overline mb-0.5">&nbsp;</div>
                <AttachWorkflowButton lead={lead}
                                      onAttached={async () => {
                                        const r = await axios.get(`${API_BASE}/leads/${lead.lead_id}`);
                                        setLead(r.data);
                                        onUpdated();
                                      }} />
              </div>
            </div>
            <FeeBreakdown lead={lead} onChanged={(updated) => { setLead(updated); onUpdated(); }} />
          </div>

          {/* Interested services + service referrals */}
          <div className="border-t border-[var(--border)] pt-4">
            <div className="overline mb-2">Interested services</div>
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => {
                const on = lead.scenario_tags?.includes(t);
                return (
                  <button key={t} onClick={() => patch({
                    scenario_tags: on
                      ? lead.scenario_tags.filter((x) => x !== t)
                      : [...(lead.scenario_tags||[]), t]
                  })} data-testid={`lead-detail-tag-${t}`}
                    className={`text-[11px] px-2 py-1 rounded-full border ${
                      on ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]" : "border-[var(--border)] text-[var(--ink-2)] hover:border-[var(--ink)]"
                    }`}>{tagLabel(t)}</button>
                );
              })}
            </div>
            <ServiceReferrals lead={lead} onChanged={(updated) => { setLead(updated); onUpdated(); }} />
          </div>

          {/* Firm services catalogue tagging (v59) */}
          <div className="border-t border-[var(--border)] pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="overline">Service offerings</div>
              <a href="/settings/services" className="text-[10px] text-[var(--ink-3)] hover:text-[var(--accent)]"
                 data-testid="lead-detail-manage-services-link">Manage →</a>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="lead-detail-services">
              {services.length === 0 && (
                <div className="text-[11px] text-[var(--ink-3)]">
                  No services configured yet — <a href="/settings/services" className="underline">add some</a>.
                </div>
              )}
              {services.map((s) => {
                const on = (lead.service_ids || []).includes(s.service_id);
                return (
                  <button key={s.service_id} onClick={() => patch({
                    service_ids: on
                      ? (lead.service_ids || []).filter((x) => x !== s.service_id)
                      : [...(lead.service_ids || []), s.service_id],
                  })} title={s.description || s.name}
                    data-testid={`lead-detail-service-${s.service_id}`}
                    className={`text-[11px] px-2 py-1 rounded-full border inline-flex items-center gap-1 ${
                      on ? "bg-[var(--accent)] text-[var(--paper)] border-[var(--accent)]" : "border-[var(--border)] text-[var(--ink-2)] hover:border-[var(--accent)]"
                    }`}>
                    {s.code && <span className="font-mono text-[9px] opacity-80">{s.code}</span>}
                    {s.name}
                    {s.default_fee > 0 && (
                      <span className={`font-mono text-[9px] ${on ? "opacity-80" : "text-[var(--ink-3)]"}`}>
                        ${Number(s.default_fee).toLocaleString()}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Year tagging (v59) — multi-select calendar years */}
          <div className="border-t border-[var(--border)] pt-4">
            <div className="overline mb-2">Year(s)</div>
            <div className="flex flex-wrap gap-2" data-testid="lead-detail-years">
              {(() => {
                const now = new Date().getFullYear();
                const opts = [now + 1, now, now - 1, now - 2, now - 3, now - 4];
                return opts.map((y) => {
                  const on = (lead.years || []).includes(y);
                  return (
                    <button key={y} onClick={() => patch({
                      years: on
                        ? (lead.years || []).filter((x) => x !== y)
                        : [...(lead.years || []), y].sort((a, b) => b - a),
                    })} data-testid={`lead-detail-year-${y}`}
                      className={`text-[11px] px-2.5 py-1 rounded-full border font-mono ${
                        on ? "bg-[var(--moss)] text-[var(--paper)] border-[var(--moss)]" : "border-[var(--border)] text-[var(--ink-2)] hover:border-[var(--moss)]"
                      }`}>{y}</button>
                  );
                });
              })()}
            </div>
          </div>

          {/* Deferred fields */}
          {lead.status === "deferred" && (() => {
            const r = DEFERRAL_REASONS.find((x) => x.key === lead.deferral_reason);
            const reasonLabel = r ? r.label : (lead.deferral_reason || "—");
            return (
              <div className="card p-3" style={{ background: "rgba(166,59,34,0.05)" }} data-testid="lead-deferred-block">
                <div className="overline mb-2" style={{ color: "#B91C1C" }}>Deferred</div>
                <div className="text-[12px] text-[var(--ink)]"><b>Reason:</b> {reasonLabel}
                  {lead.deferral_reason === "other" && lead.deferral_reason_other && (
                    <span className="text-[var(--ink-2)]"> — {lead.deferral_reason_other}</span>
                  )}
                </div>
                <div className="text-[12px] text-[var(--ink)]"><b>Follow-up:</b> {lead.follow_up_at || "—"}</div>
              </div>
            );
          })()}

          {/* Conversion link */}
          {lead.client_id && (
            <div className="card p-3" style={{ background: "var(--subtle)" }} data-testid="lead-conversion-block">
              <div className="overline mb-1 text-[10px]" style={{ color: "var(--moss)" }}>Converted</div>
              <div className="text-[12px]"><a href={`/clients`} className="text-[var(--accent)] hover:underline">Client {lead.client_id}</a> · <a href={`/jobs`} className="text-[var(--accent)] hover:underline">Job {lead.job_id}</a></div>
            </div>
          )}

          {/* Tasks */}
          <div className="border-t border-[var(--border)] pt-4">
            <TasksPanel parentKind="lead" parentId={lead.lead_id} />
          </div>

          {/* Comments + @mentions */}
          <div className="border-t border-[var(--border)] pt-4">
            <CommentsPanel kind="lead" entityId={lead.lead_id} />
          </div>

          {/* v68.36 — Referrals to external partners */}
          <div className="border-t border-[var(--border)] pt-4">
            <LeadReferralsSection leadId={lead.lead_id} />
          </div>

          {/* v68.41 — Unified activity timeline (comms + system + manual) */}
          <div className="border-t border-[var(--border)] pt-4">
            <div className="overline mb-1 inline-flex items-center gap-1 px-5"><Activity size={11}/> Activity</div>
            <ActivityTimeline scope="lead" id={lead.lead_id} />
          </div>

          {/* Timeline + notes */}
          <div className="border-t border-[var(--border)] pt-4">
            <div className="overline mb-2 inline-flex items-center gap-1"><MessageSquare size={11}/> Timeline & notes</div>
            <div className="flex gap-2 mb-3">
              <input value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Add an internal note…" className={`${INPUT_CLS} flex-1`} data-testid="lead-note-input"/>
              <button onClick={addNote} disabled={!noteText.trim()} className="btn-secondary" data-testid="lead-note-submit"><Plus size={12}/></button>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="lead-timeline">
              {(lead.timeline || []).slice().reverse().map((e, i) => (
                <div key={`${e.at}-${i}`} className="text-[12px] border-l-2 border-[var(--border)] pl-3 py-1">
                  <div className="text-[10px] font-mono text-[var(--ink-3)] mb-0.5">{new Date(e.at).toLocaleString()} · {e.kind}</div>
                  <div className="text-[var(--ink-2)]">
                    {e.kind === "note" && e.payload?.body}
                    {e.kind === "status_changed" && <>From <i>{e.payload?.from}</i> → <b>{e.payload?.to}</b>{e.payload?.reason && ` (${e.payload.reason})`}</>}
                    {e.kind === "created" && <>Lead created from {e.payload?.source}</>}
                    {e.kind === "updated" && Object.keys(e.payload || {}).filter((k)=>k!=="updated_at").join(", ")}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-[var(--border)] pt-4 flex justify-between">
            <button onClick={remove} className="text-[11px] text-[var(--ink-3)] hover:text-[#B91C1C]" data-testid="lead-detail-delete">Delete lead</button>
          </div>
        </div>
      </div>
      {sendingEmail && (
        <ComposeEmailDialog
          open={sendingEmail}
          onClose={() => setSendingEmail(false)}
          onSent={() => { setSendingEmail(false); reload(); onUpdated(); }}
          context={{ lead_id: leadId, client_id: lead?.client_id || undefined }}
          defaultTo={lead?.email || ""}
          defaultToName={lead?.name || ""}
        />
      )}
      {deferModal && (
        <DeferLeadModal
          onClose={() => setDeferModal(null)}
          onSubmit={async ({ reason, reason_other, follow_up_at }) => {
            const ok = await setStatus(deferModal.newStatus,
              { deferral_reason: reason, deferral_reason_other: reason_other,
                follow_up_at }, false);
            setDeferModal(null);
            return ok;
          }}
        />
      )}
    </div>
  );
}

function ConvertButton({ lead, onConverted }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    if (!window.confirm(`Convert "${lead.name}" to a Client?\nThis will mark the lead as lodged and create a Client + Job.`)) return;
    setBusy(true);
    try {
      const { data: conv } = await axios.post(`${API_BASE}/leads/${lead.lead_id}/convert`);
      checkIdGaps({ jobIds: [conv.job_id], source: "Lead converted" });
      const refreshed = await axios.get(`${API_BASE}/leads/${lead.lead_id}`);
      onConverted(refreshed.data);
      toast.success("Lead converted to client");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid="lead-detail-convert"
      className="px-3 py-2 rounded-md text-[13px] font-medium disabled:opacity-50 w-full"
      style={{ background: "var(--ink)", color: "var(--paper)" }}
    >
      {busy ? "Converting…" : "Convert to Client →"}
    </button>
  );
}

function AttachWorkflowButton({ lead, onAttached }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const openPicker = async () => {
    setOpen(true);
    if (templates.length) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/workflows`);
      setTemplates(data || []);
    } catch (e) { toast.error(formatApiError(e) || "Could not load workflows"); }
    finally { setLoading(false); }
  };
  const attach = async (tpl) => {
    setBusyId(tpl.template_id);
    try {
      const { data } = await axios.post(`${API_BASE}/leads/${lead.lead_id}/attach-workflow`, {
        template_id: tpl.template_id,
      });
      toast.success(
        data.reused
          ? `${tpl.name} was already attached — reusing job.`
          : `${tpl.name} attached · job created.`,
      );
      setOpen(false);
      if (!data.reused) checkIdGaps({ jobIds: [data.job_id, data.initial_job_id], source: "Workflow attached" });
      onAttached && onAttached();
    } catch (e) { toast.error(formatApiError(e) || "Attach failed"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={openPicker}
        data-testid="lead-detail-attach-workflow"
        className="px-3 py-2 rounded-md text-[13px] font-medium w-full inline-flex items-center justify-center gap-1.5 border"
        style={{ borderColor: "var(--border)", color: "var(--ink)", background: "white" }}
      >
        <GitBranch size={13} /> Attach workflow
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.35)" }}
          onClick={() => setOpen(false)}
          data-testid="attach-workflow-modal"
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-[var(--border)] flex items-start justify-between">
              <div>
                <div className="overline mb-1">Attach a workflow</div>
                <h3 className="font-heading text-lg" style={{ fontWeight: 500 }}>
                  Pick a template for {lead.name}
                </h3>
                <p className="text-[12px] text-[var(--ink-2)] mt-1">
                  Converts this lead to a client (if needed) and spawns a job from the template.
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="text-[var(--ink-3)] hover:text-[var(--ink)]">
                <X size={16} />
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2">
              {loading ? (
                <div className="text-[12px] text-[var(--ink-3)] flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" /> Loading templates…
                </div>
              ) : templates.length === 0 ? (
                <div className="text-[12px] text-[var(--ink-3)] italic">
                  No workflow templates yet. Install one from Settings → Workflows.
                </div>
              ) : templates.map((tpl) => (
                <button
                  key={tpl.template_id}
                  onClick={() => attach(tpl)}
                  disabled={busyId === tpl.template_id}
                  data-testid={`attach-workflow-tpl-${tpl.template_id}`}
                  className="w-full text-left border rounded-md p-3 hover:border-[var(--ink)] transition disabled:opacity-50"
                  style={{ borderColor: "var(--border)" }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-[13px]" style={{ color: "var(--ink)" }}>{tpl.name}</div>
                    {busyId === tpl.template_id
                      ? <Loader2 size={12} className="animate-spin text-[var(--ink-3)]" />
                      : <ChevronRight size={13} className="text-[var(--ink-3)]" />}
                  </div>
                  {tpl.description && (
                    <div className="text-[11px] text-[var(--ink-2)] mt-1 line-clamp-2">{tpl.description}</div>
                  )}
                  <div className="text-[10px] text-[var(--ink-3)] mt-1 font-mono">
                    {tpl.stages?.length || 0} stages · {(tpl.default_budget_minutes ?? 0)}m budget
                    {typeof tpl.instance_count === "number" && ` · ${tpl.instance_count} in flight`}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function ServiceReferrals({ lead, onChanged }) {
  const [members, setMembers] = useState([]);
  const [picker, setPicker] = useState({ tag: "", user_id: "" });
  const [busy, setBusy] = useState(false);
  const refs = lead.service_referrals || [];

  useEffect(() => {
    let cancel = false;
    axios.get(`${API_BASE}/team/members`).then((r) => { if (!cancel) setMembers(r.data || []); }).catch(() => {});
    return () => { cancel = true; };
  }, []);

  const submit = async () => {
    if (!picker.tag || !picker.user_id) {
      toast.error("Pick both a service and a teammate");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/leads/${lead.lead_id}/service-referrals`, {
        tag: picker.tag, referred_to_user_id: picker.user_id,
      });
      const r = await axios.get(`${API_BASE}/leads/${lead.lead_id}`);
      onChanged(r.data);
      setPicker({ tag: "", user_id: "" });
      toast.success("Teammate tagged");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Referral failed");
    } finally { setBusy(false); }
  };

  const removeRef = async (ref_id) => {
    try {
      await axios.delete(`${API_BASE}/leads/${lead.lead_id}/service-referrals/${ref_id}`);
      const r = await axios.get(`${API_BASE}/leads/${lead.lead_id}`);
      onChanged(r.data);
    } catch (e) {
      toast.error("Remove failed");
    }
  };

  if (!lead.scenario_tags?.length) return null;

  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
      <div className="overline mb-2">Service referrals</div>
      {refs.length > 0 && (
        <div className="space-y-1 mb-3" data-testid="service-referrals-list">
          {refs.map((r) => (
            <div key={r.ref_id} className="flex items-center justify-between gap-2 text-[12px] px-2 py-1.5 rounded"
                 style={{ background: "var(--subtle)" }} data-testid={`service-ref-${r.ref_id}`}>
              <div>
                <span className="font-medium">{r.tag}</span>
                <span className="text-[var(--ink-3)]"> → </span>
                <span>{r.referred_to_user_name || r.referred_to_user_id}</span>
              </div>
              <button
                type="button"
                onClick={() => removeRef(r.ref_id)}
                data-testid={`service-ref-remove-${r.ref_id}`}
                className="text-[var(--ink-3)] hover:text-[var(--accent)] text-[11px]"
              >Remove</button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <select
          value={picker.tag}
          onChange={(e) => setPicker({ ...picker, tag: e.target.value })}
          data-testid="service-ref-tag-select"
          className="flex-1 px-2 py-1.5 rounded border bg-transparent text-[12px]"
          style={{ borderColor: "var(--border)" }}
        >
          <option value="">— Service —</option>
          {lead.scenario_tags.map((t) => (<option key={t} value={t}>{t}</option>))}
        </select>
        <select
          value={picker.user_id}
          onChange={(e) => setPicker({ ...picker, user_id: e.target.value })}
          data-testid="service-ref-user-select"
          className="flex-1 px-2 py-1.5 rounded border bg-transparent text-[12px]"
          style={{ borderColor: "var(--border)" }}
        >
          <option value="">— Teammate —</option>
          {members.map((m) => (<option key={m.user_id} value={m.user_id}>{m.name}</option>))}
        </select>
        <button
          type="button"
          onClick={submit}
          disabled={busy || !picker.tag || !picker.user_id}
          data-testid="service-ref-add"
          className="px-3 py-1.5 rounded text-[12px] font-medium disabled:opacity-50"
          style={{ background: "var(--ink)", color: "var(--paper)" }}
        >Tag</button>
      </div>
    </div>
  );
}

function FeeBreakdown({ lead, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const items = lead.fee_items || [];
  const total = lead.estimated_fee || 0;
  const kindLabel = {
    base: "Base",
    tag_addon: "Tag",
    multi_year: "Multi-year",
    additional: "Manual",
  };
  const kindColor = {
    base: "var(--ink-3)",
    tag_addon: "var(--accent)",
    multi_year: "var(--moss)",
    additional: "var(--ink)",
  };

  const recalc = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/leads/${lead.lead_id}/calculate-fee`);
      const full = await axios.get(`${API_BASE}/leads/${lead.lead_id}`);
      onChanged(full.data);
      toast.success(`Recalculated — $${r.data.estimated_fee}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const addItem = async () => {
    const amt = parseFloat(amount);
    if (!label.trim() || !Number.isFinite(amt) || amt === 0) { toast.error("Label and a non-zero amount are required (negatives ok for discounts)"); return; }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/leads/${lead.lead_id}/fee-items`, { label: label.trim(), amount: amt });
      const full = await axios.get(`${API_BASE}/leads/${lead.lead_id}`);
      onChanged(full.data);
      setLabel(""); setAmount(""); setAdding(false);
      toast.success("Fee item added");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const removeItem = async (item) => {
    if (item.kind !== "additional") return;
    if (!window.confirm(`Remove "${item.label}" ($${item.amount})?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API_BASE}/leads/${lead.lead_id}/fee-items/${item.item_id}`);
      const full = await axios.get(`${API_BASE}/leads/${lead.lead_id}`);
      onChanged(full.data);
      toast.success("Fee item removed");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const startEdit = (item) => {
    setEditingId(item.item_id);
    setEditLabel(item.label);
    setEditAmount(String(item.amount));
  };
  const saveEdit = async () => {
    const amt = parseFloat(editAmount);
    if (!editLabel.trim() || !Number.isFinite(amt) || amt === 0) {
      toast.error("Label and a non-zero amount are required (negatives ok)"); return;
    }
    setBusy(true);
    try {
      await axios.patch(`${API_BASE}/leads/${lead.lead_id}/fee-items/${editingId}`,
                        { label: editLabel.trim(), amount: amt });
      const full = await axios.get(`${API_BASE}/leads/${lead.lead_id}`);
      onChanged(full.data);
      setEditingId(null); setEditLabel(""); setEditAmount("");
      toast.success("Fee item updated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--subtle)] p-3" data-testid="fee-breakdown">
      <div className="flex items-center justify-between mb-2">
        <div className="overline inline-flex items-center gap-1 text-[10px]">
          <Calculator size={11}/> Fee breakdown
        </div>
        <div className="flex items-center gap-1.5">
          <a href={`${API_BASE}/leads/${lead.lead_id}/quote.pdf`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[var(--ink-3)] hover:text-[var(--accent)] inline-flex items-center gap-1" data-testid="fee-quote-pdf-btn">
            <FileText size={10}/> Quote PDF
          </a>
          <Link to="/fee-schedule" className="text-[10px] text-[var(--ink-3)] hover:text-[var(--accent)] hover:underline" data-testid="fee-schedule-link">
            Edit schedule
          </Link>
          <button onClick={recalc} disabled={busy} className="text-[10px] text-[var(--ink-3)] hover:text-[var(--accent)] inline-flex items-center gap-1" data-testid="fee-recalc-btn">
            <RefreshCw size={10} className={busy ? "animate-spin" : ""}/> Recalc
          </button>
        </div>
      </div>
      <div className="space-y-1 mb-2" data-testid="fee-items-list">
        {items.length === 0 && <div className="text-[11px] text-[var(--ink-3)] italic">No fee items yet — add scenario tags to trigger auto-calc.</div>}
        {items.map((i) => (
          <div key={i.item_id} className="flex items-center justify-between text-[12px] py-1 border-b border-[var(--border)] last:border-b-0" data-testid={`fee-item-${i.item_id}`}>
            {editingId === i.item_id ? (
              <div className="flex gap-1.5 items-center flex-1">
                <input value={editLabel} onChange={(e) => setEditLabel(e.target.value)}
                       className={`${INPUT_CLS} text-[12px] py-1 flex-1`}
                       data-testid={`fee-item-edit-label-${i.item_id}`} />
                <input value={editAmount} onChange={(e) => setEditAmount(e.target.value)}
                       type="number" step="0.01"
                       className={`${INPUT_CLS} text-[12px] py-1 w-24`}
                       data-testid={`fee-item-edit-amount-${i.item_id}`} />
                <button onClick={saveEdit} disabled={busy}
                        className="btn-primary px-2 py-1 text-[10px]"
                        data-testid={`fee-item-edit-save-${i.item_id}`}>Save</button>
                <button onClick={() => setEditingId(null)} className="btn-secondary px-2 py-1 text-[10px]">
                  <X size={10}/>
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "var(--paper)", color: kindColor[i.kind] || "var(--ink-3)", border: `1px solid ${kindColor[i.kind] || "var(--border)"}` }}>
                    {kindLabel[i.kind] || i.kind}
                  </span>
                  <span className="truncate text-[var(--ink-2)]">{i.label}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`font-mono ${i.amount < 0 ? "text-[#B91C1C]" : "text-[var(--ink)]"}`}>
                    {i.amount < 0 ? `-$${Math.abs(i.amount).toFixed(2)}` : `$${i.amount.toFixed(2)}`}
                  </span>
                  {i.kind === "additional" && (
                    <>
                      <button onClick={() => startEdit(i)}
                              className="text-[var(--ink-3)] hover:text-[var(--accent)]"
                              data-testid={`fee-item-edit-${i.item_id}`}
                              title="Edit">
                        <Pencil size={11}/>
                      </button>
                      <button onClick={() => removeItem(i)} className="text-[var(--ink-3)] hover:text-[#B91C1C]"
                              data-testid={`fee-item-remove-${i.item_id}`}>
                        <Trash2 size={11}/>
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between border-t border-[var(--border)] pt-2 mb-2">
        <span className="text-[11px] uppercase tracking-wider text-[var(--ink-3)]">Total estimated</span>
        <span className="font-mono text-[15px] font-semibold text-[var(--ink)]" data-testid="fee-total">
          ${Number(total || 0).toFixed(2)}
        </span>
      </div>
      {adding ? (
        <div className="flex gap-1.5 items-end" data-testid="fee-item-add-form">
          <div className="flex-1">
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. Late penalty · Manual discount)" className={`${INPUT_CLS} text-[12px] py-1.5`} data-testid="fee-item-label-input"/>
          </div>
          <div className="w-28">
            <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" step="0.01" placeholder="$ (negative ok)" className={`${INPUT_CLS} text-[12px] py-1.5`} data-testid="fee-item-amount-input"/>
          </div>
          <button onClick={addItem} disabled={busy} className="btn-primary px-2 py-1.5 text-[11px]" data-testid="fee-item-add-submit">Add</button>
          <button onClick={() => { setAdding(false); setLabel(""); setAmount(""); }} className="btn-secondary px-2 py-1.5 text-[11px]">
            <X size={11}/>
          </button>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} className="text-[11px] text-[var(--accent)] hover:underline inline-flex items-center gap-1" data-testid="fee-item-add-btn">
          <Plus size={11}/> Add manual item
        </button>
      )}
    </div>
  );
}

function WebhookConfigModal({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/lead-webhook/config`);
      setCfg(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch { toast.error("Copy failed — select and copy manually"); }
  };

  const rotate = async () => {
    if (!window.confirm("Rotate the webhook token? Any existing integration using the current token will stop working until updated.")) return;
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/lead-webhook/rotate`);
      await load();
      toast.success("Token rotated — update your integrations");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const curlExample = cfg ? `curl -X POST '${cfg.url}' \\
  -H 'Content-Type: application/json' \\
  -d '${JSON.stringify(cfg.example_payload, null, 2).replace(/'/g, "\\'")}'` : "";

  return (
    <div className="fixed inset-0 z-50 bg-[var(--ink)]/40 flex items-center justify-center p-6" onClick={onClose} data-testid="webhook-config-modal">
      <div className="bg-[var(--paper)] rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[var(--border)]" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl inline-flex items-center gap-2" style={{ fontWeight: 500 }}>
              <Webhook size={18}/> Inbound webhook
            </h2>
            <p className="text-[11px] text-[var(--ink-3)] mt-1">Point your website contact form, Zapier, or Make.com here. Lead is created automatically with auto-calc + dedup.</p>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="webhook-close"><X size={18}/></button>
        </div>
        {!cfg ? (
          <div className="p-6 text-[12px] text-[var(--ink-3)]">Loading…</div>
        ) : (
          <div className="p-6 space-y-4">
            <div>
              <div className="overline mb-1 text-[10px]">Endpoint URL</div>
              <div className="flex gap-2 items-start">
                <code className="flex-1 text-[11px] bg-[var(--subtle)] border border-[var(--border)] rounded-md p-2 break-all font-mono" data-testid="webhook-url">{cfg.url}</code>
                <button onClick={() => copy(cfg.url, "URL")} className="btn-secondary px-2 py-1.5 text-[11px]" data-testid="webhook-url-copy"><Copy size={11}/></button>
              </div>
            </div>
            <div>
              <div className="overline mb-1 text-[10px] flex items-center justify-between">
                <span>Token</span>
                <button onClick={rotate} disabled={busy} className="text-[10px] text-[#B91C1C] hover:underline inline-flex items-center gap-1" data-testid="webhook-rotate">
                  <RotateCw size={10} className={busy ? "animate-spin" : ""}/> Rotate
                </button>
              </div>
              <div className="flex gap-2 items-start">
                <code className="flex-1 text-[11px] bg-[var(--subtle)] border border-[var(--border)] rounded-md p-2 break-all font-mono" data-testid="webhook-token">{cfg.token}</code>
                <button onClick={() => copy(cfg.token, "Token")} className="btn-secondary px-2 py-1.5 text-[11px]" data-testid="webhook-token-copy"><Copy size={11}/></button>
              </div>
            </div>
            <div>
              <div className="overline mb-1 text-[10px]">Example curl</div>
              <pre className="text-[11px] bg-[var(--ink)] text-[var(--paper)] rounded-md p-3 overflow-x-auto font-mono" data-testid="webhook-curl">{curlExample}</pre>
              <button onClick={() => copy(curlExample, "Snippet")} className="text-[10px] text-[var(--accent)] hover:underline mt-1" data-testid="webhook-curl-copy">Copy snippet</button>
            </div>
            <div className="text-[10px] text-[var(--ink-3)] border-t border-[var(--border)] pt-3 space-y-1">
              <div>• Accepts common alt field names: <code>full_name</code>, <code>email_address</code>, <code>mobile</code>, <code>message</code>, <code>utm_source</code>, etc.</div>
              <div>• Duplicate emails/phones return <code>{`{status:"duplicate"}`}</code> — no new lead created.</div>
              <div>• Every request is logged for audit (<code>lead_webhook_log</code>).</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function IntakeFormConfigModal({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/intake-form/config`);
      setCfg(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!cfg) return;
    setBusy(true);
    try {
      const payload = {
        enabled: cfg.enabled,
        slug: cfg.slug,
        heading: cfg.heading || null,
        blurb: cfg.blurb || null,
        success_message: cfg.success_message || null,
        accent_color: cfg.accent_color || null,
      };
      const r = await axios.put(`${API_BASE}/intake-form/config`, payload);
      // Re-fetch to get fresh public_url with possibly new slug
      const r2 = await axios.get(`${API_BASE}/intake-form/config`);
      setCfg(r2.data);
      toast.success("Intake form saved" + (r.data?.slug !== cfg.slug ? " — new public URL" : ""));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--ink)]/40 flex items-center justify-center p-6" onClick={onClose} data-testid="intake-config-modal">
      <div className="bg-[var(--paper)] rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[var(--border)]" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl inline-flex items-center gap-2" style={{ fontWeight: 500 }}>
              <Globe size={18}/> Hosted intake form
            </h2>
            <p className="text-[11px] text-[var(--ink-3)] mt-1">A branded public page clients can submit through — no website required.</p>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="intake-config-close"><X size={18}/></button>
        </div>
        {!cfg ? (
          <div className="p-6 text-[12px] text-[var(--ink-3)]">Loading…</div>
        ) : (
          <div className="p-6 space-y-4">
            {/* Public URL */}
            <div>
              <div className="overline mb-1 text-[10px]">Public URL</div>
              <div className="flex gap-2 items-start">
                <code className="flex-1 text-[11px] bg-[var(--subtle)] border border-[var(--border)] rounded-md p-2 break-all font-mono" data-testid="intake-public-url">{cfg.public_url}</code>
                <button onClick={() => copy(cfg.public_url, "URL")} className="btn-secondary px-2 py-1.5 text-[11px]" data-testid="intake-url-copy"><Copy size={11}/></button>
                <a href={cfg.public_url} target="_blank" rel="noopener noreferrer" className="btn-secondary px-2 py-1.5 text-[11px]" data-testid="intake-open-link"><ExternalLink size={11}/></a>
              </div>
            </div>

            {/* Enabled toggle */}
            <label className="flex items-center gap-2 cursor-pointer border border-[var(--border)] rounded-md p-3">
              <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="accent-[var(--accent)]" data-testid="intake-enabled-toggle"/>
              <div>
                <div className="text-[13px] text-[var(--ink)]">Form is live</div>
                <div className="text-[10px] text-[var(--ink-3)]">When unchecked, the public URL returns a &quot;currently disabled&quot; page.</div>
              </div>
            </label>

            {/* Slug */}
            <label className="block">
              <div className="overline mb-1 text-[10px]">Slug</div>
              <input value={cfg.slug} onChange={(e) => setCfg({ ...cfg, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px] font-mono"
                placeholder="your-firm" data-testid="intake-slug-input"/>
              <div className="text-[10px] text-[var(--ink-3)] mt-1">Lowercase letters, numbers, hyphens only. Changing this breaks any existing link.</div>
            </label>

            {/* Heading */}
            <label className="block">
              <div className="overline mb-1 text-[10px]">Heading</div>
              <input value={cfg.heading || ""} onChange={(e) => setCfg({ ...cfg, heading: e.target.value })}
                className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px]"
                placeholder="Get a tax-return quote" data-testid="intake-heading-input"/>
            </label>

            {/* Blurb */}
            <label className="block">
              <div className="overline mb-1 text-[10px]">Intro paragraph</div>
              <textarea value={cfg.blurb || ""} onChange={(e) => setCfg({ ...cfg, blurb: e.target.value })}
                rows={3} className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px]"
                data-testid="intake-blurb-input"/>
            </label>

            {/* Success */}
            <label className="block">
              <div className="overline mb-1 text-[10px]">Success message</div>
              <input value={cfg.success_message || ""} onChange={(e) => setCfg({ ...cfg, success_message: e.target.value })}
                className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px]"
                data-testid="intake-success-input"/>
            </label>

            {/* Accent */}
            <label className="block">
              <div className="overline mb-1 text-[10px]">Brand accent color</div>
              <div className="flex items-center gap-2">
                <input type="color" value={cfg.accent_color || "#B91C1C"} onChange={(e) => setCfg({ ...cfg, accent_color: e.target.value })}
                  className="w-12 h-9 rounded border border-[var(--border)] cursor-pointer" data-testid="intake-accent-input"/>
                <code className="text-[12px] font-mono text-[var(--ink-3)]">{cfg.accent_color || "(default)"}</code>
                {cfg.accent_color && (
                  <button onClick={() => setCfg({ ...cfg, accent_color: null })} className="text-[10px] text-[var(--ink-3)] hover:text-[var(--accent)]" data-testid="intake-accent-reset">Reset</button>
                )}
              </div>
            </label>
          </div>
        )}
        <div className="p-6 border-t border-[var(--border)] flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary" data-testid="intake-config-cancel">Close</button>
          <button onClick={save} disabled={busy || !cfg} className="btn-primary" data-testid="intake-config-save">
            {busy ? <Loader2 size={14} className="animate-spin"/> : null}
            {busy ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

